import { getMartData } from "api/ApiCalls"
import { applyFetcherOptions } from "helpers/martHelpers"
import { type MartFetcherOptions } from "types/ApiTypes"
import type {
  IServerSideGetRowsRequest,
  SortModelItem,
} from "@tokenterminal/ui/DataTable"

const DATASET_TABLE = `project_contracts`

const PLAIN_SSRM_REQUEST: IServerSideGetRowsRequest = {
  startRow: 0,
  endRow: 100,
  valueCols: [],
  pivotCols: [],
  rowGroupCols: [],
  pivotMode: false,
  groupKeys: [],
  filterModel: {},
  sortModel: [],
}

type DataGridSortModelItem = SortModelItem & {
  isSortCaseInsensitive?: boolean
}

type ProjectContractsGetRowsRequest = Omit<
  IServerSideGetRowsRequest,
  "sortModel"
> & {
  sortModel: Array<DataGridSortModelItem>
}

const DEFAULT_SSRM_REQUEST: ProjectContractsGetRowsRequest = {
  ...PLAIN_SSRM_REQUEST,
  sortModel: [
    {
      sort: "asc",
      colId: "project_name",
      isSortCaseInsensitive: true,
    },
    { sort: "desc", colId: "gas_used_30d_sum" },
  ],
  rowGroupCols: [
    {
      displayName: "Project",
      field: "project_name",
      id: "project_name",
    },
  ],
}

type ProjectContractsDataBQ = {
  project_id: string | null
  project_name: string | null
  chain_id: string | null
  chain_name: string | null
  market_sector_id: string | null
  market_sector_name: string | null
  most_emitted_event: string | null
  account_address: string | null
  account_title: string | null
  account_creation_timestamp: { value: string | null }

  active_users_1d_distinct_count: number | null
  active_users_1d_distinct_count_trend: number | null
  active_users_7d_distinct_count: number | null
  active_users_7d_distinct_count_trend: number | null
  active_users_30d_distinct_count: number | null
  active_users_30d_distinct_count_trend: number | null
  active_users_90d_distinct_count: number | null
  active_users_90d_distinct_count_trend: number | null
  active_users_180d_distinct_count: number | null
  active_users_180d_distinct_count_trend: number | null
  active_users_365d_distinct_count: number | null
  active_users_365d_distinct_count_trend: number | null

  gas_market_share_1d_avg: number | null
  gas_market_share_1d_avg_trend: number | null
  gas_market_share_7d_avg: number | null
  gas_market_share_7d_avg_trend: number | null
  gas_market_share_30d_avg: number | null
  gas_market_share_30d_avg_trend: number | null
  gas_market_share_90d_avg: number | null
  gas_market_share_90d_avg_trend: number | null
  gas_market_share_180d_avg: number | null
  gas_market_share_180d_avg_trend: number | null
  gas_market_share_365d_avg: number | null
  gas_market_share_365d_avg_trend: number | null

  gas_used_1d_sum: number | null
  gas_used_1d_sum_trend: number | null
  gas_used_7d_sum: number | null
  gas_used_7d_sum_trend: number | null
  gas_used_30d_sum: number | null
  gas_used_30d_sum_trend: number | null
  gas_used_90d_sum: number | null
  gas_used_90d_sum_trend: number | null
  gas_used_180d_sum: number | null
  gas_used_180d_sum_trend: number | null
  gas_used_365d_sum: number | null
  gas_used_365d_sum_trend: number | null

  inflows_1d_sum: number | null
  inflows_1d_sum_trend: number | null
  inflows_7d_sum: number | null
  inflows_7d_sum_trend: number | null
  inflows_30d_sum: number | null
  inflows_30d_sum_trend: number | null
  inflows_90d_sum: number | null
  inflows_90d_sum_trend: number | null
  inflows_180d_sum: number | null
  inflows_180d_sum_trend: number | null
  inflows_365d_sum: number | null
  inflows_365d_sum_trend: number | null

  net_flows_1d_sum: number | null
  net_flows_1d_sum_trend: number | null
  net_flows_7d_sum: number | null
  net_flows_7d_sum_trend: number | null
  net_flows_30d_sum: number | null
  net_flows_30d_sum_trend: number | null
  net_flows_90d_sum: number | null
  net_flows_90d_sum_trend: number | null
  net_flows_180d_sum: number | null
  net_flows_180d_sum_trend: number | null
  net_flows_365d_sum: number | null
  net_flows_365d_sum_trend: number | null

  outflows_1d_sum: number | null
  outflows_1d_sum_trend: number | null
  outflows_7d_sum: number | null
  outflows_7d_sum_trend: number | null
  outflows_30d_sum: number | null
  outflows_30d_sum_trend: number | null
  outflows_90d_sum: number | null
  outflows_90d_sum_trend: number | null
  outflows_180d_sum: number | null
  outflows_180d_sum_trend: number | null
  outflows_365d_sum: number | null
  outflows_365d_sum_trend: number | null

  transaction_count_1d_sum: number | null
  transaction_count_1d_sum_trend: number | null
  transaction_count_7d_sum: number | null
  transaction_count_7d_sum_trend: number | null
  transaction_count_30d_sum: number | null
  transaction_count_30d_sum_trend: number | null
  transaction_count_90d_sum: number | null
  transaction_count_90d_sum_trend: number | null
  transaction_count_180d_sum: number | null
  transaction_count_180d_sum_trend: number | null
  transaction_count_365d_sum: number | null
  transaction_count_365d_sum_trend: number | null

  transaction_gas_1d_avg: number | null
  transaction_gas_1d_avg_trend: number | null
  transaction_gas_7d_avg: number | null
  transaction_gas_7d_avg_trend: number | null
  transaction_gas_30d_avg: number | null
  transaction_gas_30d_avg_trend: number | null
  transaction_gas_90d_avg: number | null
  transaction_gas_90d_avg_trend: number | null
  transaction_gas_180d_avg: number | null
  transaction_gas_180d_avg_trend: number | null
  transaction_gas_365d_avg: number | null
  transaction_gas_365d_avg_trend: number | null
}

// Export still uses this, there's a TODO to remvoe
export async function getProjectContractsData<
  T = Array<ProjectContractsDataBQ>,
>(
  body: IServerSideGetRowsRequest = DEFAULT_SSRM_REQUEST,
  opts?: MartFetcherOptions
) {
  if (opts) {
    applyFetcherOptions(body, opts)
  }

  const martData = await getMartData<T>(DATASET_TABLE, body)

  return martData.data
}
